import React, {useState, useEffect, useRef} from "react";
import GenericItem from "../../common/items/GenericItem";
import BackButton from "../../common/buttons/BackButton";
import GenericButton from "../../common/buttons/GenericButton";
import {Settings} from "../../../models/models";
import PlanToggleSwitchRow from "../../common/subscriptions/PlanToggleSwitchRow";
import {convertPriceUnitAmountToReadable} from "../../../helpers/helpers";
import {PAYMENT_TYPE} from "../../../constants";


const PlanSelectPage = (props: {
    setData: Function,
    settings: Settings,
    payNow: boolean,
    setPage: Function,
    selectedPlan: Object,
    setSelectedPlan: Function,
    isEligibleForFreeTrial: boolean,
    pages: Object,
    paymentType: Object
}) => {
    const {
        data,
        setData,
        pages,
        setPage,
        selectedPlan,
        setSelectedPlan,
        isEligibleForFreeTrial,
        paymentType
    } = props;

    const firstUpdate = useRef(true);

    const {doNotRenew, levels = [], featureLabels} = data || {};

    const [selectedDoNotRenew, setSelectedDoNotRenew] = useState(doNotRenew);
    const [wantsAnnual, setWantsAnnual] = useState(false);

    const plusLevel = levels[0] || null;
    const maxScanCountPlus = plusLevel?.maxScanCount || null;
    const featureLabelsPlus = plusLevel?.featureLabels || [];

    const basicLevel = levels[1] || null;
    const maxScanCountBasic = basicLevel?.maxScanCount || null;
    const featureLabelsBasic = basicLevel?.featureLabels || [];

    if (!selectedPlan) {
        const {label, monthly, yearly} = levels[0];

        const paymentFormatData = wantsAnnual ? yearly : monthly;
        setSelectedPlan({label, ...paymentFormatData});
    }

    useEffect(() => {
        if (firstUpdate.current) {
            firstUpdate.current = false;
            return;
        }

        setData({...data, doNotRenew: selectedDoNotRenew});
    }, [selectedDoNotRenew]);

    const onContinueClick = () => {
        setPage(pages.CHECKOUT);
    };

    const onWantsAnnualToggle = () => {
        setWantsAnnual(val => !val);
        setSelectedPlan(null);
    }

    const vdsPlanOptions = paymentType === PAYMENT_TYPE.VDS;

    return (
        <div data-testid={'planSelectPageDiv'}>
            {
                !isEligibleForFreeTrial && (
                    <PlanToggleSwitchRow selectedDoNotRenew={selectedDoNotRenew}
                                         setSelectedDoNotRenew={setSelectedDoNotRenew}/>
                )
            }

            <GenericItem title={i18n.payments.selectAPlan}
                         details={''}/>

            <fieldset style={{display: "grid", gridAutoFlow: "column", width: "fit-content", columnGap: "25px"}}>
                <div>
                    <input type="radio" id="monthly" name="planSelect" checked={wantsAnnual ? '' : 'checked'}
                           onChange={onWantsAnnualToggle}/>
                    <label style={{paddingLeft: "5px"}} htmlFor="monthly">{i18n.payments.monthly}</label>
                </div>
                <div>
                    <input type="radio" id="annual" name="planSelect" checked={wantsAnnual ? 'checked' : ''}
                           onChange={onWantsAnnualToggle}/>
                    <label style={{paddingLeft: "5px"}} htmlFor="annual">{i18n.payments.annual}</label>
                </div>
            </fieldset>

            <div className={'flexColumn'}>
                <div className={'row'}>
                    {
                        levels.map((level, idx) => {
                            const {label, monthly, yearly} = level;

                            const paymentFormatData = wantsAnnual ? yearly : monthly;
                            const {value, priceAmount} = paymentFormatData;

                            const amount = convertPriceUnitAmountToReadable(priceAmount);
                            const priceFormatLabel = wantsAnnual ? i18n.payments.year : i18n.payments.month;
                            const priceLabel = `$${amount} / ${priceFormatLabel}`;

                            const onClick = () => {
                                setSelectedPlan({label, ...paymentFormatData});
                            };

                            let selected = false;

                            if (!selectedPlan) {
                                if (value === 29 || value === 15)
                                    selected = true;
                            } else {
                                selected = selectedPlan?.value === value;
                            }

                            return (
                                <div className={'col-6 plan-select-row flexColumn'} key={`level-${level.label}-${idx}`}>
                                    <div className={`vri-plan-card flexColumn ${selected ? 'selected' : 'unselected'}`}
                                         data-testid={'planSelectCardDiv'}
                                         data-wants-annual={wantsAnnual}
                                         data-plan-select-card-value={value}
                                         onClick={onClick}>
                                        <div className={'plan-sub-title'}>{level.label}</div>
                                        <div className={'plan-sub-details'}>{i18n.payments.pricing}</div>
                                        <div className={'plan-sub-title'}>{priceLabel}</div>
                                    </div>
                                </div>
                            );
                        })
                    }
                </div>

                <div style={{display: "flex", gridColumnGap: "10px"}}>
                    <div>
                        <GenericItem title={"Plan Benefits"}
                                     details={''}/>

                        <div className={'plan-select-body'}>
                            <div className={'flexColumn'} style={{alignSelf: "center"}}>
                                <div className={'plan-select-card'} style={{width: "max-content", borderWidth: "0"}}>
                                    <div className={'plan-sub-details'}>{i18n.payments.techSupport}</div>
                                    <div className={'plan-sub-title'}>
                                        Monday - Friday
                                    </div>
                                    <div className={'plan-sub-title'}>
                                        8:00am - 12:00pm
                                    </div>
                                    <div className={'plan-sub-title'}>
                                        1:00pm - 4:30pm
                                    </div>
                                    <div className={'plan-sub-title'}>
                                        Pacific Time
                                    </div>
                                </div>
                            </div>

                            {
                                vdsPlanOptions && (
                                    <table className={'plan-display-table'}
                                           data-testid={'vdsPlanDisplayTableDiv'}>
                                        <tbody>
                                        <tr style={{textAlign: "center"}}>
                                            <th>

                                            </th>
                                            <th>
                                                Plus
                                            </th>
                                            <th>
                                                Basic
                                            </th>
                                        </tr>
                                        <tr style={{
                                            border: "solid lightgrey",
                                            borderWidth: "0 0 2px 0"
                                        }}>
                                            <td style={{padding: "5px"}}>
                                                <div className={'plan-sub-details'} style={{
                                                    gridColumn: 1,
                                                    gridRow: featureLabelsPlus.length
                                                }}>{i18n.payments.numOfScans}</div>
                                            </td>
                                            <td style={{padding: "5px"}}>
                                                <div className={'plan-sub-title'} style={{
                                                    gridColumn: 2,
                                                    gridRow: featureLabelsPlus.length
                                                }}>{maxScanCountPlus}</div>
                                            </td>
                                            <td style={{padding: "5px"}}>
                                                <div className={'plan-sub-title'} style={{
                                                    gridColumn: 3,
                                                    gridRow: featureLabelsPlus.length
                                                }}>{maxScanCountBasic}</div>
                                            </td>
                                        </tr>
                                        {
                                            featureLabelsPlus?.map(({label, value}, index) => {
                                                const imgSrcPlus = value ? '../images/Green-Check-Circle.png' : '../images/Red-X-Circle.png';
                                                const imgSrcBasic = featureLabelsBasic[index].value ? '../images/Green-Check-Circle.png' : '../images/Red-X-Circle.png';
                                                if (index === featureLabelsPlus.length - 1) {
                                                    return (
                                                        <tr>
                                                            <td style={{padding: "5px"}}>
                                                                <div className={'plan-sub-details'}>{label}</div>
                                                            </td>
                                                            <td style={{padding: "5px", textAlign: "center"}}>
                                                                <img loading="lazy"
                                                                     className="plan-tag-img"
                                                                     src={imgSrcPlus}/>
                                                            </td>
                                                            <td style={{padding: "5px", textAlign: "center"}}>
                                                                <img loading="lazy"
                                                                     className="plan-tag-img"
                                                                     src={imgSrcBasic}/>
                                                            </td>
                                                        </tr>
                                                    );
                                                } else {
                                                    return (
                                                        <tr
                                                            style={{
                                                                border: "solid lightgrey",
                                                                borderWidth: "0 0 2px 0"
                                                            }}>
                                                            <td style={{padding: "5px"}}>
                                                                <div className={'plan-sub-details'}>{label}</div>
                                                            </td>
                                                            <td style={{padding: "5px", textAlign: "center"}}>
                                                                <img loading="lazy"
                                                                     className="plan-tag-img"
                                                                     src={imgSrcPlus}/>
                                                            </td>
                                                            <td style={{padding: "5px", textAlign: "center"}}>
                                                                <img loading="lazy"
                                                                     className="plan-tag-img"
                                                                     src={imgSrcBasic}/>
                                                            </td>
                                                        </tr>
                                                    );
                                                }
                                            })
                                        }
                                        </tbody>
                                    </table>
                                )
                            }
                            {
                                !vdsPlanOptions && (
                                    <div className={'plan-select-feature-labels'}
                                         data-testid={'vriPlanDisplayTableDiv'}
                                         style={{width: "fit-content"}}>
                                        {
                                            featureLabels?.map(({label, value}, index) => {
                                                const imgSrc = value ? '../images/Green-Check-Circle.png' : '../images/Red-X-Circle.png';
                                                if (index === featureLabels.length - 1) {
                                                    return (
                                                        <div className={'plan-select-card flexSpaceBetween'}
                                                             style={{flexDirection: 'row', borderWidth: "0"}}>
                                                            <div className={'plan-sub-details'}>{label}</div>
                                                            <img loading="lazy"
                                                                 className="plan-tag-img"
                                                                 src={imgSrc}/>
                                                        </div>
                                                    );
                                                } else {
                                                    return (
                                                        <div className={'plan-select-card flexSpaceBetween'}
                                                             style={{flexDirection: 'row'}}>
                                                            <div className={'plan-sub-details'}>{label}</div>
                                                            <img loading="lazy"
                                                                 className="plan-tag-img"
                                                                 src={imgSrc}/>
                                                        </div>
                                                    );
                                                }
                                            })
                                        }
                                    </div>
                                )
                            }
                        </div>
                    </div>
                </div>
            </div>

            <div className={"row"}>
                <div className={"col-12"}>
                    <div className={"flexSpaceBetween footerMargins"}>
                        <BackButton onClick={() => setPage(pages.SPLASH)}/>
                        <GenericButton onClick={onContinueClick}
                                       disabled={selectedPlan === null}
                                       testid={'continueBtn'}
                                       text={i18n.common.continue}/>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PlanSelectPage;